import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import React from "react";
import { useTranslation } from "react-i18next";
import { TranslationPrefixes } from "consts/translationPrefixes";
import { ProductDataRow } from "./types";

const useStyles = makeStyles((theme) => ({
  table: { minWidth: 800 },
  container: { maxHeight: 400 },
  title: { marginBottom: theme.spacing(1) },
  cellNarrow: { maxWidth: 120, overflow: "hidden", textOverflow: "ellipsis" },
  image: { width: 50, height: 50, objectFit: "cover", borderRadius: 4 },
}));

interface Props {
  products: ProductDataRow[];
  currencySymbol: string;
  maxHeight?: number;
}

export default function ProductDataResults({ products, currencySymbol, maxHeight = 520 }: Props) {
  const classes = useStyles();
  const containerStyle = maxHeight ? { maxHeight } : undefined;
  const { t } = useTranslation();
  const p = TranslationPrefixes.productDataList;

  const fmt = (n: number) =>
    Number.isFinite(n) ? n.toLocaleString(undefined, { maximumFractionDigits: 2 }) : "-";
  const money = (n: number) =>
    `${currencySymbol}${fmt(n)}`;

  return (
    <>
      <Typography variant="h6" className={classes.title}>
        {t(`${TranslationPrefixes.productDataService}resultsTitle`)}
      </Typography>
      <TableContainer className={classes.container} style={containerStyle}>
        <Table className={classes.table} size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>{t(`${p}asin`)}</TableCell>
              <TableCell>{t(`${p}title`)}</TableCell>
              <TableCell>{t(`${p}imageUrl`)}</TableCell>
              <TableCell align="right">{t(`${p}salePrice`)}</TableCell>
              <TableCell align="right">{t(`${p}amazonFees`)}</TableCell>
              <TableCell align="right">{t(`${p}reviewCount`)}</TableCell>
              <TableCell align="right">{t(`${p}averageRating`)}</TableCell>
              <TableCell>{t(`${p}category`)}</TableCell>
              <TableCell align="right">{t(`${p}categoryRank`)}</TableCell>
              <TableCell>{t(`${p}sellerType`)}</TableCell>
              <TableCell>{t(`${p}brand`)}</TableCell>
              <TableCell align="right">{t(`${p}sales30Days`)}</TableCell>
              <TableCell align="right">{t(`${p}revenue30Days`)}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {products.map((row) => (
              <TableRow key={row.asin}>
                <TableCell className={classes.cellNarrow}>{row.asin}</TableCell>
                <TableCell className={classes.cellNarrow} title={row.title}>
                  {row.title.length > 40 ? `${row.title.slice(0, 40)}…` : row.title}
                </TableCell>
                <TableCell>
                  {row.imageUrl ? (
                    <img src={row.imageUrl} alt="" className={classes.image} />
                  ) : (
                    "-"
                  )}
                </TableCell>
                <TableCell align="right">{money(row.salePrice)}</TableCell>
                <TableCell align="right">{money(row.amazonFees)}</TableCell>
                <TableCell align="right">{fmt(row.reviewCount)}</TableCell>
                <TableCell align="right">{fmt(row.averageRating)}</TableCell>
                <TableCell className={classes.cellNarrow}>{row.category}</TableCell>
                <TableCell align="right">{fmt(row.categoryRank)}</TableCell>
                <TableCell>{row.sellerType}</TableCell>
                <TableCell>{row.brand}</TableCell>
                <TableCell align="right">{fmt(row.sales30Days)}</TableCell>
                <TableCell align="right">{money(row.revenue30Days)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
}
